/**
 * @file Generic Button Component.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

const propTypes = {
  btnStyle: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'success-vibrant',
    'info',
    'warning',
    'danger',
    'glass',
    'link',
    'reset',
    'yellow',
    'white',
    'gray',
    'orange',
    'gold',
    'active-disabled',
  ]),
  btnSize: PropTypes.oneOf(['xs', 'sm', 'lg']),
  btnOutline: PropTypes.bool,
  btnBlock: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  href: PropTypes.string,
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      hash: PropTypes.string,
      state: PropTypes.object,
    }),
  ]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  value: PropTypes.string,
  children: PropTypes.node,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  sticky: PropTypes.oneOf(['bottom', 'top']),
  pill: PropTypes.bool,
  loading: PropTypes.bool,
};

const Button = ({
  btnStyle = 'primary',
  btnSize,
  btnOutline,
  btnBlock,
  type = 'button',
  href,
  disabled,
  className,
  value,
  to,
  children,
  inputRef,
  sticky,
  pill,
  loading,
  ...props
}) => {
  const classes = classNames({
    btn: btnStyle !== 'reset',
    [`btn-${btnStyle}`]: btnStyle && !btnOutline,
    [`btn-outline-${btnStyle}`]: btnOutline,
    [`btn-${btnSize}`]: btnSize,
    'btn-block': btnBlock || !!sticky,
    'btn-noradius': !!sticky,
    'sticky-bottom': sticky === 'bottom',
    'sticky-top': sticky === 'top',
    'btn-pill': !!pill,
    disabled: disabled && (href || to),
    [className]: className,
  });

  const anchor = to ? (
    <Link
      {...props}
      className={classes}
      to={to}
      aria-disabled={disabled || undefined}
      innerRef={inputRef}
    >
      {children}
    </Link>
  ) : (
    <a
      {...props}
      className={classes}
      href={href}
      aria-disabled={disabled || undefined}
      ref={inputRef}
    >
      {children}
    </a>
  );

  const button = (
    <button
      {...props}
      type={type}
      className={classes}
      disabled={disabled}
      ref={inputRef}
    >
      {children}
    </button>
  );

  const input = (
    <input
      {...props}
      type={type}
      value={value}
      className={classes}
      disabled={disabled}
      ref={inputRef}
    />
  );

  if (href || to) {
    return anchor;
  }

  if (value) {
    return input;
  }

  return button;
};

Button.propTypes = propTypes;

export default Button;
